import {
  Box,
  Group,
  Alert,
  Divider,
  createStyles,
  Title,
  Container,
  Button,
  Stack,
  Text,
  Anchor,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { planTypes, planReasons, planLocations, hearFromUs, liraProvinces } from "./selectOptions"
import { PhoneNumberInput, validatePhoneNumber } from "~/forms/components/PhoneNumberInput"
import { SelectInput } from "~/forms/components/SelectInput"
import { StringInput } from "~/forms/components/StringInput"
import { NumberInput } from "~/forms/components/NumberInput"
import { YesNoInput } from "~/forms/components/YesNoInput"
import { UseMutationResult } from "react-query"
import { pickInitialValues } from "~/forms/utils"
import { FormRules } from "@mantine/form/lib/types"
import pick from "lodash.pick"
import { Prisma } from "@prisma/client"
import { useMemo, useState } from "react"
import { EditingContext } from "~/forms/editingContext"
import { ApplicationModel } from "~/application/model"
import { trpc } from "~/trpc/client"
import Link from "next/link"
import { regionsName } from "~/application/countries"
import { brand } from "~/brand"

const getFormValues = (application: ApplicationModel) =>
  pick(
    application,
    "planType",
    "planTypeOther",
    "planReason",
    "planReasonOther",
    "planLocation",
    "planLocationOther",
    "liraProvince",
    "planValue",
    "amountToUnlock",
    "requireAdvance",
    "advanceAmount",
    "firstName",
    "email",
    "phoneNumber",
    "hearFromUs",
    "hearFromUsOther"
  )

const initialValues = pickInitialValues({
  planType: ["", "lira"],
  planTypeOther: [""],
  planReason: ["", "stopped-working"],
  planReasonOther: [""],
  planLocation: ["", "bank"],
  planLocationOther: [""],
  liraProvince: ["", "ON"],
  planValue: [null, new Prisma.Decimal(50000)],
  amountToUnlock: [null, new Prisma.Decimal(25000)],
  requireAdvance: [null, true],
  advanceAmount: [null, new Prisma.Decimal(2000)],
  firstName: ["", "Alex"],
  email: ["", "alex.turpin@gmail.com"],
  phoneNumber: ["", "8198675309"],
  hearFromUs: ["", "google"],
  hearFromUsOther: [""],
})

export type PrequalFormValues = {
  planType: string
  planTypeOther: string
  planReason: string
  planReasonOther: string
  planLocation: string
  planLocationOther: string
  liraProvince: string | null
  planValue: Prisma.Decimal | null
  amountToUnlock: Prisma.Decimal | null
  requireAdvance: boolean | null
  advanceAmount: Prisma.Decimal | null
  firstName: string
  email: string
  phoneNumber: string
  hearFromUs: string
  hearFromUsOther: string
}

const validate: FormRules<PrequalFormValues> = {
  planType: (value) => (value ? null : "Please select an option"),
  planTypeOther: (value, { planType }) =>
    planType !== "other" || value.trim() ? null : "Please enter what type of pension plan you have",
  planReason: (value) => (value ? null : "Please select an option"),
  planReasonOther: (value, { planReason }) =>
    planReason !== "other" || value.trim()
      ? null
      : "Please enter why you received your pension plan",
  planLocation: (value) => (value ? null : "Please select an option"),
  planLocationOther: (value, { planLocation }) =>
    planLocation !== "other" || value.trim() ? null : "Please enter who has your pension plan",
  liraProvince: (value, { planType }) =>
    planType !== "lira" || value ? null : "Please select an option",
  planValue: (value) =>
    value !== null && value.gte(20000) ? null : "The minimum plan value is $20,000",
  amountToUnlock: (value, { planValue }) =>
    value === null || value.lt(20000)
      ? "The minimum amount to unlock is $20,000"
      : planValue !== null && value.gt(planValue)
      ? "The amount to unlock must be less than the total value of the plan"
      : null,
  requireAdvance: (value) => (value !== null ? null : "Please select an option"),
  advanceAmount: (value, { requireAdvance }) =>
    requireAdvance === false || (value && value.gte(1000) && value.lte(5000))
      ? null
      : "Please enter an amount between $1,000 and $5,000",
  firstName: (value) => (value.trim() ? null : "Please enter a first name"),
  email: (value) =>
    !value.trim()
      ? "This field is required"
      : !/^\S+@\S+\.\S+$/.test(value)
      ? "Please enter a valid email address"
      : null,
  phoneNumber: (value) => validatePhoneNumber(value),
  hearFromUs: (value) => (value ? null : "Please select an option"),
  hearFromUsOther: (value, { hearFromUs }) =>
    (hearFromUs !== "other" && hearFromUs !== "friends-family") || value !== ""
      ? null
      : "Please enter where you heard from us",
}

const useStyles = createStyles(() => ({
  md: {
    maxWidth: "350px",
    width: "100%",
  },
  lg: {
    maxWidth: "400px",
    width: "100%",
  },
}))

type PrequalFormProps = {
  application?: ApplicationModel
  mutation: Pick<UseMutationResult<unknown>, "isLoading" | "isError">
  save: (prequal: PrequalFormValues) => any
  admin?: boolean
}

export const PrequalForm = ({ application, mutation, save, admin = false }: PrequalFormProps) => {
  const form = useForm<PrequalFormValues>({
    initialValues: application ? getFormValues(application) : initialValues,
    validate,
  })
  const { values, setFieldValue } = form

  const hasErrors = useMemo(() => Object.keys(form.errors).length > 0, [form.errors])
  const [editing, setEditing] = useState(false)
  const [navigating, setNavigating] = useState(false)

  const { classes } = useStyles()

  const isLoggedIn = trpc.useQuery(["application.isLoggedIn"])

  return (
    <Container size="sm" my="xl">
      <form
        onSubmit={async (event) => {
          event.preventDefault()

          const { hasErrors } = form.validate()
          if (hasErrors) return

          setNavigating(true)

          try {
            await save(values)
          } catch (err) {
            setNavigating(false)
            throw err
          }
        }}
        noValidate
      >
        {admin && (
          <>
            {mutation.isError && (
              <Alert color="red" mb="md">
                There was an error saving the form.
              </Alert>
            )}
            <Group position="right">
              {editing ? (
                <Button
                  loading={mutation?.isLoading}
                  onClick={async () => {
                    await save(values)
                    setEditing(false)
                  }}
                >
                  Save
                </Button>
              ) : (
                <Button onClick={() => setEditing(true)}>Edit</Button>
              )}
            </Group>
          </>
        )}

        {admin && ( // We show a centered title on the home page
          <Title order={2} mb="md">
            Pre-qualification questionnaire
          </Title>
        )}

        {!admin && typeof isLoggedIn.data === "number" && (
          <Alert color="yellow" mb="md">
            Our records indicate that you have already completed the pre-qualification
            questionnaire.{" "}
            <Link href={`/application/${isLoggedIn.data}`} passHref>
              <Anchor size="sm">Click here to go to your results.</Anchor>
            </Link>
          </Alert>
        )}

        <EditingContext.Provider value={!admin || editing}>
          <Stack>
            <SelectInput
              form={form}
              field="planType"
              label="What type of pension plan do you have?"
              data={planTypes}
              onChange={() => {
                setFieldValue("planTypeOther", "")
                setFieldValue("liraProvince", "")
              }}
              required
              className={classes.lg}
            />

            {values.planType === "other" && (
              <StringInput
                form={form}
                field="planTypeOther"
                adminLabel="What type of pension plan do you have? (other)"
                placeholder="Please enter what type of pension plan you have"
                className={classes.lg}
              />
            )}

            <SelectInput
              form={form}
              field="planReason"
              label="Why did you receive your pension plan?"
              data={planReasons}
              onChange={() => {
                setFieldValue("planReasonOther", "")
              }}
              required
              className={classes.lg}
            />

            {values.planReason === "other" && (
              <StringInput
                form={form}
                field="planReasonOther"
                adminLabel="Why did you receive your pension plan? (other)"
                placeholder="Please enter why you received your pension plan"
                className={classes.lg}
              />
            )}

            <SelectInput
              form={form}
              field="planLocation"
              label="Who has the pension plan currently?"
              data={planLocations}
              onChange={() => {
                setFieldValue("planLocationOther", "")
              }}
              required
              className={classes.lg}
            />

            {values.planLocation === "other" && (
              <StringInput
                form={form}
                field="planLocationOther"
                adminLabel="Who has the pension plan currently? (other)"
                placeholder="Please enter who has your pension plan"
                className={classes.lg}
              />
            )}

            {values.planType === "lira" && (
              <SelectInput
                form={form}
                field="liraProvince"
                label="Under which province was your LIRA locked under?"
                placeholder={regionsName("CA")}
                data={liraProvinces}
                required
                className={classes.lg}
              />
            )}

            <NumberInput
              form={form}
              field="planValue"
              label="What is the current value of your pension plan?"
              description="Minimum plan value: $20,000"
              required
              className={classes.md}
            />

            <NumberInput
              form={form}
              field="amountToUnlock"
              label="How much do you want to unlock?"
              description="Minimum unlock amount: $20,000"
              required
              className={classes.md}
            />

            <YesNoInput
              form={form}
              field="requireAdvance"
              onChange={() => {
                setFieldValue("advanceAmount", null)
              }}
              label="Do you need a cash advance to address an immediate financial emergency?"
              description="Depending on your plan value, advances range from $1,000 to $5,000."
              required
              sx={{ width: "100%" }}
            />

            {(values.requireAdvance || admin) && (
              <NumberInput
                form={form}
                field="advanceAmount"
                label="How much of an advance do you need?"
                required
                className={classes.md}
              />
            )}

            <Divider />

            <Group grow={true} align="flex-start" sx={{ width: "100%" }}>
              <StringInput form={form} field="firstName" label="First name" required />
              <Box>{/* for alignment */}</Box>
            </Group>

            <Group grow={true} align="flex-start" sx={{ width: "100%" }}>
              <StringInput form={form} field="email" label="Email" required />
              <PhoneNumberInput form={form} field="phoneNumber" label="Phone number" required />
            </Group>

            <SelectInput
              form={form}
              field="hearFromUs"
              label="How did you hear about us?"
              data={hearFromUs}
              onChange={() => {
                setFieldValue("hearFromUsOther", "")
              }}
              required
              className={classes.lg}
            />

            {(values.hearFromUs === "other" || values.hearFromUs === "friends-family") && (
              <StringInput
                form={form}
                field="hearFromUsOther"
                adminLabel="How did you hear about us? (other)"
                placeholder={
                  values.hearFromUs === "friends-family"
                    ? "Please enter the name of the friend or family member"
                    : "Please enter where you heard about us"
                }
                required
                className={classes.lg}
              />
            )}
          </Stack>
        </EditingContext.Provider>

        {!admin && (
          <Text
            color="dimmed"
            size="sm"
            mb="md"
            mt="lg"
            align="center"
            sx={{ fontStyle: "italic" }}
          >
            There is no commitment and we do not ask for any personal information that could
            identify you such as social insurance number or your date of birth. We do not sell or
            communicate your information, such as your email address to any other organization
            unless you give us permission and this will only be requested of you if you qualify for
            any options and wish to use one of our referral partners to obtain assistance in
            unlocking or accessing funds in your locked plan.
          </Text>
        )}

        {!admin && (
          <>
            {mutation.isError && (
              <Alert color="red">
                There was an error submitting your form. Please try again or{" "}
                <a href={`mailto:${brand.mailto}`}>contact us</a> if the problem persists.
              </Alert>
            )}

            {hasErrors && (
              <Alert color="red">
                Please review the form and correct any errors before continuing.
              </Alert>
            )}

            <Group position="right" mt="lg">
              <Button type="submit" loading={mutation?.isLoading || navigating}>
                Submit
              </Button>
            </Group>
          </>
        )}
      </form>
    </Container>
  )
}
